import React, { useState, useEffect } from 'react';
import { API, authFetch } from '../utils/api';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, PieChart, Pie, Cell, ResponsiveContainer } from 'recharts';
import CustomModal from './CustomModal';

var COLORS = ['#6366f1', '#f59e0b', '#888'];
function fmtMan(n) { return Math.round((n || 0) / 10000).toLocaleString(); }

function ReportPage({ showToast, user }) {
  var now = new Date();
  var [month, setMonth] = useState(now.toISOString().slice(0, 7));
  var [data, setData] = useState(null);
  var [loading, setLoading] = useState(false);
  var [pdfLoading, setPdfLoading] = useState(false);
  var [sendLoading, setSendLoading] = useState(false);
  var [modal, setModal] = useState(null);
  var closeModal = function() { setModal(null); };
  var isAdmin = !user || user.role === '관리자' || user.role === 'admin';

  var fetchReport = function(m) {
    setLoading(true);
    authFetch(API + '/reports/monthly/' + m).then(function(r) { return r.json(); }).then(function(d) {
      if (d.success) setData(d.data);
      else setData(null);
      setLoading(false);
    }).catch(function() { setData(null); setLoading(false); });
  };

  useEffect(function() { fetchReport(month); }, [month]);

  var downloadPdf = function() {
    setPdfLoading(true);
    authFetch(API + '/reports/monthly/' + month + '/pdf').then(function(r) {
      if (!r.ok) throw new Error('PDF 생성 실패');
      return r.blob();
    }).then(function(blob) {
      var a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = 'AUTODEX_Report_' + month + '.pdf';
      a.click();
      URL.revokeObjectURL(a.href);
      setPdfLoading(false);
    }).catch(function() { showToast('PDF 생성 실패', 'err'); setPdfLoading(false); });
  };

  var sendMonthly = function() {
    setModal({ type: 'confirm', title: '전송할까요?', message: month + ' 리포트를 전 업체에 발송합니다', onConfirm: function() {
      closeModal();
      setSendLoading(true);
      authFetch(API + '/reports/send-monthly', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ month: month })
      }).then(function(r) { return r.json(); }).then(function(d) {
        if (d.success) {
          var ok = d.results.filter(function(r) { return r.success; }).length;
          showToast(ok + '개 업체 발송 완료', 'ok');
        } else { showToast('발송 실패', 'err'); }
        setSendLoading(false);
      }).catch(function() { showToast('발송 실패', 'err'); setSendLoading(false); });
    } });
  };

  var cardStyle = { background: '#fff', borderRadius: 12, border: '1px solid #eee', padding: 16, marginBottom: 12 };

  // 월 선택 생성
  var months = [];
  for (var i = 0; i < 12; i++) {
    var d = new Date();
    d.setDate(1);
    d.setMonth(d.getMonth() - i);
    months.push(d.toISOString().slice(0, 7));
  }

  if (loading) return <div style={{ textAlign: 'center', padding: '60px 0', color: '#aaa' }}>로딩 중...</div>;

  return (
    <div style={{ maxWidth: 900, margin: '0 auto', padding: '16px 12px' }}>
      {/* 헤더 */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <h2 style={{ margin: 0, fontSize: 20, fontWeight: 800, color: '#111' }}>월간 리포트</h2>
        <div style={{ display: 'flex', gap: 8 }}>
          <select value={month} onChange={function(e) { setMonth(e.target.value); }} style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid #e0dfda', fontSize: 14 }}>
            {months.map(function(m) { return <option key={m} value={m}>{m.split('-')[0]}년 {parseInt(m.split('-')[1])}월</option>; })}
          </select>
          <button onClick={downloadPdf} disabled={pdfLoading || !data} style={{ padding: '8px 16px', borderRadius: 8, border: 'none', background: pdfLoading ? '#aaa' : '#1B3A5C', color: '#fff', cursor: pdfLoading ? 'default' : 'pointer', fontSize: 13, fontWeight: 600 }}>
            {pdfLoading ? '생성중...' : 'PDF'}
          </button>
          {isAdmin && (
            <button onClick={sendMonthly} disabled={sendLoading} style={{ padding: '8px 16px', borderRadius: 8, border: 'none', background: sendLoading ? '#aaa' : '#D85A30', color: '#fff', cursor: sendLoading ? 'default' : 'pointer', fontSize: 13, fontWeight: 600 }}>
              {sendLoading ? '발송중...' : '전체 발송'}
            </button>
          )}
        </div>
      </div>

      {!data && <div style={{ textAlign: 'center', padding: '60px 0', color: '#aaa', fontSize: 14 }}>리포트 데이터가 없습니다.</div>}

      {modal && <CustomModal isOpen={true} {...modal} onCancel={closeModal} />}
      {data && (
        <div>
          {/* 처리 현황 */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 10, marginBottom: 14 }}>
            {[
              [data.status.newRegistered, '신규 등록', '#6366f1'],
              [data.status.cancelCompleted, '말소 완료', '#10b981'],
              [data.status.clearanceCompleted, '면장 완료', '#3b82f6'],
              [data.status.shipped, '선적 완료', '#22c55e']
            ].map(function(item, i) {
              return (
                <div key={i} style={{ ...cardStyle, textAlign: 'center', padding: '14px 0' }}>
                  <div style={{ fontSize: 28, fontWeight: 800, color: item[2] }}>{item[0]}</div>
                  <div style={{ fontSize: 11, color: '#888' }}>{item[1]}</div>
                </div>
              );
            })}
          </div>

          {/* 금액 요약 */}
          <div style={cardStyle}>
            <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 12, color: '#222' }}>금액 요약</div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              <div style={{ background: '#f8f8f5', borderRadius: 8, padding: '10px 14px' }}>
                <div style={{ fontSize: 10, color: '#888' }}>총 매입가 (도착가)</div>
                <div style={{ fontSize: 18, fontWeight: 700 }}>{fmtMan(data.amount.totalCost)}만원</div>
              </div>
              <div style={{ background: '#f8f8f5', borderRadius: 8, padding: '10px 14px' }}>
                <div style={{ fontSize: 10, color: '#888' }}>총 판매가</div>
                <div style={{ fontSize: 18, fontWeight: 700 }}>{fmtMan(data.amount.totalSell)}만원</div>
              </div>
              <div style={{ background: data.amount.profit >= 0 ? '#f0fdf4' : '#fef2f2', borderRadius: 8, padding: '10px 14px' }}>
                <div style={{ fontSize: 10, color: '#888' }}>예상 수익</div>
                <div style={{ fontSize: 18, fontWeight: 700, color: data.amount.profit >= 0 ? '#10b981' : '#E24B4A' }}>{fmtMan(data.amount.profit)}만원</div>
              </div>
              <div style={{ background: '#eff6ff', borderRadius: 8, padding: '10px 14px' }}>
                <div style={{ fontSize: 10, color: '#888' }}>부가세 환급 예상</div>
                <div style={{ fontSize: 18, fontWeight: 700, color: '#3b82f6' }}>{fmtMan(data.amount.vatRefund)}만원</div>
              </div>
            </div>
            <div style={{ fontSize: 11, color: '#aaa', marginTop: 8 }}>
              차량 경비: {(data.amount.carExpenses || 0).toLocaleString()}원 · 말소대행비: {(data.amount.cancelFees || 0).toLocaleString()}원 · 일반경비: {(data.amount.generalExpenses || 0).toLocaleString()}원
            </div>
          </div>

          {/* 차트: 월별 추이 + 매입유형 */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 14 }}>
            <div style={cardStyle}>
              <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 8, color: '#222' }}>최근 6개월 추이</div>
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={(data.monthlyTrend || []).map(function(t) {
                  return { name: parseInt(t.month.split('-')[1]) + '월', 등록: t.registered, 말소: t.cancelled, 면장: t.cleared, 선적: t.shipped };
                })}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" fontSize={11} />
                  <YAxis fontSize={11} allowDecimals={false} />
                  <Tooltip />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  <Bar dataKey="등록" fill="#6366f1" />
                  <Bar dataKey="말소" fill="#10b981" />
                  <Bar dataKey="면장" fill="#3b82f6" />
                  <Bar dataKey="선적" fill="#22c55e" />
                </BarChart>
              </ResponsiveContainer>
            </div>
            <div style={cardStyle}>
              <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 8, color: '#222' }}>매입유형 비율</div>
              <ResponsiveContainer width="100%" height={220}>
                <PieChart>
                  <Pie
                    data={[
                      { name: '개인', value: data.purchaseTypes.individual || 0 },
                      { name: '사업자', value: data.purchaseTypes.business || 0 },
                      { name: '기타', value: data.purchaseTypes.other || 0 }
                    ].filter(function(d) { return d.value > 0; })}
                    cx="50%" cy="50%" outerRadius={80} dataKey="value" label={function(p) { return p.name + ' ' + p.value + '대'; }}
                  >
                    {[0, 1, 2].map(function(i) { return <Cell key={i} fill={COLORS[i]} />; })}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* 바이어별 매출 */}
          {data.buyerSales && data.buyerSales.length > 0 && (
            <div style={cardStyle}>
              <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 8, color: '#222' }}>바이어별 매출</div>
              {data.buyerSales.map(function(b, i) {
                return (
                  <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', borderBottom: i < data.buyerSales.length - 1 ? '1px solid #f0f0ec' : 'none' }}>
                    <div>
                      <span style={{ fontSize: 14, fontWeight: 600, color: '#222' }}>{b.name || '-'}</span>
                      <span style={{ fontSize: 12, color: '#888', marginLeft: 8 }}>{b.cnt}대</span>
                    </div>
                    <span style={{ fontSize: 14, fontWeight: 700, color: '#1B3A5C', fontFamily: 'monospace' }}>{(b.totalFob || 0).toLocaleString()} {b.currency || 'USD'}</span>
                  </div>
                );
              })}
            </div>
          )}

          {/* 주의 사항 */}
          <div style={cardStyle}>
            <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 8, color: '#222' }}>주의 사항</div>
            <div style={{ display: 'flex', gap: 16, marginBottom: 8 }}>
              <div style={{ padding: '8px 14px', borderRadius: 8, background: data.warnings.deadlineCount > 0 ? '#fef2f2' : '#f8f8f5', flex: 1 }}>
                <span style={{ fontSize: 11, color: '#888' }}>이행기간 임박 (D-30)</span>
                <div style={{ fontSize: 20, fontWeight: 700, color: data.warnings.deadlineCount > 0 ? '#E24B4A' : '#222' }}>{data.warnings.deadlineCount}대</div>
              </div>
              <div style={{ padding: '8px 14px', borderRadius: 8, background: data.warnings.missingDocsCount > 0 ? '#fef9e7' : '#f8f8f5', flex: 1 }}>
                <span style={{ fontSize: 11, color: '#888' }}>서류 미비</span>
                <div style={{ fontSize: 20, fontWeight: 700, color: data.warnings.missingDocsCount > 0 ? '#D85A30' : '#222' }}>{data.warnings.missingDocsCount}대</div>
              </div>
            </div>
            {data.warnings.deadlineCars && data.warnings.deadlineCars.length > 0 && (
              <div>
                <div style={{ fontSize: 12, fontWeight: 600, color: '#E24B4A', marginBottom: 4 }}>임박 차량 목록</div>
                {data.warnings.deadlineCars.map(function(c, i) {
                  var daysLeft = Math.ceil((new Date(c.export_deadline).getTime() - Date.now()) / 86400000);
                  return (
                    <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0', fontSize: 12, borderBottom: '1px solid #f0f0ec' }}>
                      <span>{c.carNo || '-'} · {c.carType || '-'}</span>
                      <span style={{ fontWeight: 600, color: daysLeft <= 0 ? '#E24B4A' : daysLeft <= 7 ? '#D85A30' : '#EF9F27' }}>
                        {c.export_deadline} (D{daysLeft <= 0 ? '+' + Math.abs(daysLeft) : '-' + daysLeft})
                      </span>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default ReportPage;
